import { useSetSettings, useSigma } from "@react-sigma/core";
import { Attributes } from "graphology-types";
import { FC, PropsWithChildren, useEffect } from "react";

import { drawHover, drawLabel } from "../canvas-utils";
import useDebounce from "../use-debounce";

const NODE_FADE_COLOR = "#bbb";
const EDGE_FADE_COLOR = "#eee";

const GraphSettingsController: FC<
  PropsWithChildren<{
    hoveredNode: string | null;
    clickedNode: string | null;
  }>
> = ({ children, hoveredNode, clickedNode }) => {
  const sigma = useSigma();
  const setSettings = useSetSettings();
  const graph = sigma.getGraph();

  // Here we debounce the value to avoid having too much highlights refresh when
  // moving the mouse over the graph:
  const debouncedHoveredNode = useDebounce(hoveredNode, 40);
  const activeNode = clickedNode || debouncedHoveredNode;

  /**
   * Initialize here settings that require to know the graph and/or the sigma
   * instance:
   */
  useEffect(() => {
    const activeColor: string =
      (activeNode && sigma.getNodeDisplayData(activeNode)?.color) || "";

    setSettings({
      defaultDrawNodeLabel: drawLabel,
      defaultDrawNodeHover: drawHover,
      nodeReducer: (node: string, data: Attributes) => {
        if (activeNode) {
          return node === activeNode ||
            graph.hasEdge(node, activeNode) ||
            graph.hasEdge(activeNode, node)
            ? { ...data, zIndex: 1 }
            : {
                ...data,
                zIndex: 0,
                label: "",
                color: NODE_FADE_COLOR,
                image: null,
                highlighted: false,
              };
        }
        return data;
      },
      edgeReducer: (edge: string, data: Attributes) => {
        if (activeNode) {
          return graph.hasExtremity(edge, activeNode)
            ? { ...data, color: activeColor, size: 4 }
            : { ...data, color: EDGE_FADE_COLOR, hidden: false };
        }
        return data;
      },
    });
  }, [sigma, graph, activeNode]);

  /**
   * Update node and edge reducers when a node is hovered, to highlight its
   * neighborhood:
   */
  useEffect(() => {
    const activeColor: string =
      (activeNode && sigma.getNodeDisplayData(activeNode)?.color) || "";

    sigma.setSetting(
      "nodeReducer",
      activeNode
        ? (node, data) =>
            node === activeNode ||
            graph.hasEdge(node, activeNode) ||
            graph.hasEdge(activeNode, node)
              ? { ...data, zIndex: 1 }
              : {
                  ...data,
                  zIndex: 0,
                  label: "",
                  color: NODE_FADE_COLOR,
                  image: null,
                  highlighted: false,
                }
        : null
    );
    sigma.setSetting(
      "edgeReducer",
      activeNode
        ? (edge, data) =>
            graph.hasExtremity(edge, activeNode)
              ? { ...data, color: activeColor, size: 4 }
              : { ...data, color: EDGE_FADE_COLOR, hidden: false }
        : null
    );
  }, [activeNode]);

  return <>{children}</>;
};

export default GraphSettingsController;
